import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import api from '../utils/api'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

export default function Landing() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await api.post('/snapshot', { name, email })
      navigate(`/snapshot/${response.data.id}`)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Something went wrong. Try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-ginger-500/15 border border-ginger-500/30 text-ginger-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ginger-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-ginger-500"></span>
              </span>
              BETA — 50 SPOTS LEFT
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-dark-100 mb-6 leading-tight">
              Everyone's getting<br />
              <span className="text-ginger-500">their exposure report.</span>
            </h1>

            <p className="text-dark-300 text-lg md:text-xl max-w-2xl mx-auto mb-4">
              The same intelligence methodology Fortune 500 CISOs pay $10,000 for
              — now available as a 2-minute scan. Free during beta.
            </p>

            <p className="text-dark-400 text-sm max-w-xl mx-auto mb-12">
              Built by intelligence professionals. Trusted by executives
              who'd rather know than wonder.
            </p>
          </motion.div>

          {/* Signup Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-md mx-auto"
          >
            <div className="card border-ginger-500/30">
              <h3 className="text-xl font-bold mb-1">Get Your Free Exposure Report</h3>
              <p className="text-dark-400 text-sm mb-6">
                See what 1,000+ sources say about you. Takes 2 minutes.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full legal name"
                  className="input"
                  required
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="input"
                  required
                />
                {error && <p className="text-red-400 text-sm">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary w-full text-base"
                >
                  {loading ? 'Scanning...' : 'Run My Scan — Free'}
                </button>
              </form>

              <p className="text-dark-500 text-xs text-center mt-4">
                No credit card. No sales call. Free during beta.
              </p>
            </div>
          </motion.div>

          {/* Social proof ticker */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 flex items-center justify-center gap-6 text-dark-500 text-xs"
          >
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              127 scans run this week
            </span>
            <span className="text-dark-700">|</span>
            <span>Avg exposure score: 71/100</span>
            <span className="text-dark-700">|</span>
            <span>93% found passwords in breaches</span>
          </motion.div>
        </div>
      </section>

      {/* What you'll know in 2 minutes */}
      <section className="py-16 px-4 bg-dark-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">What You'll Know in 2 Minutes</h2>
            <p className="text-dark-400">The things everyone else has already checked.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <LearnCard
              num="01"
              title="Your Broker Count"
              description="How many data brokers are actively selling your home address, phone number, net worth, and family connections — right now, today."
            />
            <LearnCard
              num="02"
              title="Your Breach Exposure"
              description="Which breaches contain your email and passwords. Most executives are in 15+. Some are in 50+. The number always surprises people."
            />
            <LearnCard
              num="03"
              title="Your Risk Score"
              description="A single number that tells you where you stand. Below 40, you're fine. Above 70, your peers are already taking action."
            />
          </div>
        </div>
      </section>

      {/* The reveal — sample result */}
      <section className="py-16 px-4">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-ginger-400 text-xs font-bold uppercase tracking-widest mb-3">Sample Result</p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">This Is What a Typical Report Looks Like</h2>
            <p className="text-dark-400 text-sm">Most people score between 60 and 85. Where will you land?</p>
          </div>

          <div className="card">
            <div className="flex justify-between items-center pb-4 border-b border-dark-700 mb-4">
              <span className="font-bold">Sample Executive</span>
              <span className="bg-red-500/90 text-white text-xs font-bold px-3 py-1 rounded">SCORE: 78</span>
            </div>

            <SampleRow label="Data Broker Listings" value="47 sites" severity="crit" />
            <SampleRow label="Known Breaches" value="12 breaches" severity="high" />
            <SampleRow label="Credential Exposure" value="Passwords found" severity="crit" />
            <SampleRow label="Crypto Wallets Exposed" value="2 wallets linked" severity="crit" />
            <SampleRow label="Home Address Exposed" value="31 sources" severity="crit" />
            <SampleRow label="Family Members Linked" value="3 identified" severity="high" />
            <SampleRow label="Overall Risk Score" value="78 / 100" severity="crit" />

            <div className="mt-4 p-4 border border-dashed border-ginger-500/50 rounded-lg bg-ginger-500/5 text-center">
              <p className="text-ginger-400 text-sm font-semibold">Detailed threat analysis + remediation plan</p>
              <p className="text-dark-500 text-xs mt-1">Unlocked after your scan</p>
            </div>
          </div>
        </div>
      </section>

      {/* What happens after your scan */}
      <section className="py-16 px-4 bg-dark-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-ginger-400 text-xs font-bold uppercase tracking-widest mb-3">After Your Free Scan</p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">We Don't Just Show You the Problem. We Fix It.</h2>
            <p className="text-dark-400 text-sm max-w-lg mx-auto">
              Your free scan shows you the exposure. Then you choose how far to go.
            </p>
          </div>

          {/* Tier cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {/* Free */}
            <div className="card border-dark-700">
              <div className="text-center mb-4">
                <span className="inline-block bg-green-500/15 border border-green-500/30 text-green-400 text-xs font-bold px-3 py-1 rounded-full">FREE</span>
                <h3 className="text-lg font-bold mt-3">Exposure Snapshot</h3>
                <div className="text-3xl font-black text-dark-100 mt-2">$0</div>
              </div>
              <div className="space-y-2 text-sm text-dark-400">
                <TierFeature text="Risk score across 1,000+ sources" />
                <TierFeature text="Broker count + breach count" />
                <TierFeature text="Top 5 broker opt-out links (DIY)" />
                <TierFeature text="Results in 2 minutes" />
              </div>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="btn btn-secondary w-full mt-6 text-sm"
              >
                Start Free Scan
              </button>
            </div>

            {/* $295 */}
            <div className="card border-ginger-500/40 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-ginger-500 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</span>
              </div>
              <div className="text-center mb-4">
                <span className="inline-block bg-ginger-500/15 border border-ginger-500/30 text-ginger-400 text-xs font-bold px-3 py-1 rounded-full">AUTOMATED REMOVAL</span>
                <h3 className="text-lg font-bold mt-3">Clean Sweep</h3>
                <div className="text-3xl font-black text-ginger-500 mt-2">$295</div>
              </div>
              <div className="space-y-2 text-sm text-dark-400">
                <TierFeature text="Everything in Free, plus:" accent />
                <TierFeature text="Opt-out requests to ALL brokers found" accent />
                <TierFeature text="Google search removal requests" accent />
                <TierFeature text="CCPA/GDPR breach deletion notices" accent />
                <TierFeature text="Full intelligence report" accent />
                <TierFeature text="Removal status dashboard" accent />
              </div>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="btn btn-primary w-full mt-6 text-sm"
              >
                Start Free — Upgrade After Results
              </button>
            </div>

            {/* $2,500 */}
            <div className="card border-dark-700">
              <div className="text-center mb-4">
                <span className="inline-block bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold px-3 py-1 rounded-full">EXECUTIVE</span>
                <h3 className="text-lg font-bold mt-3">Full Intelligence</h3>
                <div className="text-3xl font-black text-dark-100 mt-2">$2,500</div>
              </div>
              <div className="space-y-2 text-sm text-dark-400">
                <TierFeature text="Everything in Clean Sweep, plus:" />
                <TierFeature text="Dark web credential search" />
                <TierFeature text="Crypto wallet exposure scan" />
                <TierFeature text="Family member exposure mapping" />
                <TierFeature text="Vehicle / ALPR tracking check" />
                <TierFeature text="Asset discovery (FAA, USCG, FEC)" />
                <TierFeature text="360Privacy referral for manual removals" />
              </div>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="btn btn-secondary w-full mt-6 text-sm"
              >
                Start Free — Upgrade After Results
              </button>
            </div>
          </div>

          <p className="text-center text-dark-500 text-xs">
            Every plan starts with the free scan. No credit card required. Upgrade only after you see your results.
          </p>
        </div>
      </section>

      {/* Why now */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-3">Why Smart Executives Run This Now</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <InsightCard
              title="Board members are asking"
              detail="Cybersecurity is a board-level topic. Your personal exposure is part of your company's attack surface. Know your number before someone asks."
            />
            <InsightCard
              title="Data brokers re-list you"
              detail="Even if you've opted out before, brokers re-list within 90 days. The only way to know is to check regularly. This scan is your baseline."
            />
            <InsightCard
              title="Breach frequency is accelerating"
              detail="2025 saw more credential dumps than any prior year. Every month you wait is another month your old passwords circulate."
            />
            <InsightCard
              title="Your peers already have"
              detail="We've scanned hundreds of executives. The ones who checked early caught exposures the others are still discovering."
            />
          </div>
        </div>
      </section>

      {/* Credibility — short */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Who Built This</h2>
          <p className="text-dark-300 text-lg mb-10">
            Not a startup. A team that's been doing this for 25 years —
            and just automated the tradecraft.
          </p>

          <div className="grid grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-3xl font-bold text-ginger-500">25+</div>
              <div className="text-dark-400 text-sm mt-1">years threat intelligence</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-ginger-500">1,000+</div>
              <div className="text-dark-400 text-sm mt-1">sources per scan</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-ginger-500">$10K→Free</div>
              <div className="text-dark-400 text-sm mt-1">during beta</div>
            </div>
          </div>

          <p className="text-dark-400 text-sm max-w-lg mx-auto">
            The same methodology used to protect Olympic Games and national political conventions.
            Now available as a 2-minute scan.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-dark-900/50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Don't Be the Last to Know</h2>
          <p className="text-dark-400 mb-8">
            Beta spots are limited. Your scan takes 2 minutes. Your exposure
            report is free. The only cost is not knowing.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="btn btn-primary text-base px-8"
          >
            Run My Scan — Free
          </button>
          <p className="text-dark-500 text-xs mt-6">
            50 beta spots remaining. No obligation. No follow-up unless you ask.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  )
}

function LearnCard({ num, title, description }: { num: string; title: string; description: string }) {
  return (
    <div className="card">
      <div className="w-10 h-10 rounded-lg bg-ginger-500/15 border border-ginger-500/20 flex items-center justify-center text-ginger-500 font-bold text-sm mb-4">
        {num}
      </div>
      <h4 className="font-bold text-lg mb-2">{title}</h4>
      <p className="text-dark-400 text-sm">{description}</p>
    </div>
  )
}

function TierFeature({ text, accent }: { text: string; accent?: boolean }) {
  return (
    <div className="flex items-start gap-2">
      <svg className={`w-4 h-4 flex-shrink-0 mt-0.5 ${accent ? 'text-ginger-500' : 'text-dark-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
      <span>{text}</span>
    </div>
  )
}

function InsightCard({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="card border-dark-700 hover:border-ginger-500/40 transition-all">
      <h4 className="font-bold text-sm text-dark-100 mb-2">{title}</h4>
      <p className="text-dark-400 text-xs leading-relaxed">{detail}</p>
    </div>
  )
}

function SampleRow({ label, value, severity }: { label: string; value: string; severity: string }) {
  const colorMap: Record<string, string> = {
    crit: 'text-red-400',
    high: 'text-amber-400',
    med: 'text-ginger-400',
    ok: 'text-green-400',
  }
  return (
    <div className="flex justify-between py-2 border-b border-dark-800/50 last:border-b-0 text-sm">
      <span className="text-dark-400">{label}</span>
      <span className={`font-semibold ${colorMap[severity] || ''}`}>{value}</span>
    </div>
  )
}
